import React, { useEffect, useState } from "react";
import {
  X,
  User,
  MapPin,
  Clock,
  Users,
  BookOpen,
  Home,
  FileText,
  CheckCircle2,
  CalendarDays,
  Loader2,
  UserCheck,
  Search,
  Lock,
  AlertTriangle,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { STATUS_COLORS } from "../data/mockData";
import { getSubjectTheme } from "../subjectThemeUtils";
import {
  getAttendanceSheet,
  saveAttendanceSheet,
} from "../../../../api/elearning/attendance/attendance";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../../components/ConfirmDialog";
import { todayLocalISODate } from "../../../../utils/formatUtils";
import {
  ATTENDANCE_STATUS_BTNS,
  normalizeAttendanceStatus,
  isAbsentStatus,
  countAbsentStatuses,
} from "../../attendance/attendanceStatus";

const STATUS_BTNS = ATTENDANCE_STATUS_BTNS;

function formatHour(h) {
  const n = Number(h);
  if (Number.isNaN(n)) return String(h ?? "—");
  return `${String(Math.floor(n)).padStart(2, "0")}:00`;
}

const EventDetailModal = ({ event, onClose, sessionDate }) => {
  const [fetching, setFetching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [attendanceList, setAttendanceList] = useState([]);
  const [noteMap, setNoteMap] = useState({});
  const [sessionNote, setSessionNote] = useState("");
  const [canEdit, setCanEdit] = useState(false);
  const [isTodayDate, setIsTodayDate] = useState(true);
  const [requiresEditReason, setRequiresEditReason] = useState(false);
  const [editReason, setEditReason] = useState("");
  const [editWindowDays, setEditWindowDays] = useState(7);
  const [loadError, setLoadError] = useState(null);

  const classId = event?.class_id;
  const checkDate = sessionDate || event?.session_date || null;

  useEffect(() => {
    if (!classId || !checkDate) return;
    setFetching(true);
    setLoadError(null);
    setEditReason("");
    const dateIsToday = checkDate === todayLocalISODate();
    setIsTodayDate(dateIsToday);
    getAttendanceSheet(classId, checkDate)
      .then((data) => {
        const records = (data?.records || []).map((r) => ({
          student_id: r.student_id,
          full_name: r.full_name,
          status: normalizeAttendanceStatus(r.status),
          teacher_note: r.teacher_note || "",
        }));
        setAttendanceList(records);
        const map = {};
        records.forEach((r) => {
          map[r.student_id] = r.teacher_note;
        });
        setNoteMap(map);
        setSessionNote(data?.session_note || event?.note || "");
        setEditReason(data?.edit_reason || "");
        setRequiresEditReason(Boolean(data?.requires_edit_reason));
        setEditWindowDays(Number(data?.edit_window_days) || 7);
        setIsTodayDate(
          typeof data?.is_today === "boolean" ? data.is_today : dateIsToday
        );
        setCanEdit(typeof data?.can_edit === "boolean" ? Boolean(data.can_edit) : false);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message || "Không thể tải danh sách điểm danh";
        setLoadError(msg);
        setAttendanceList([]);
        setCanEdit(false);
        setSessionNote(event?.note || "");
      })
      .finally(() => setFetching(false));
  }, [classId, checkDate, event?.note]);

  if (!event) return null;

  const theme = getSubjectTheme(event);
  const status = STATUS_COLORS[event.status];

  const validityLabel =
    event.start_date && event.end_date
      ? `${event.start_date} → ${event.end_date}`
      : event.end_date
        ? `Đến ${event.end_date}`
        : event.start_date
          ? `Từ ${event.start_date}`
          : "";

  const infoItem = (Icon, label, value, iconColor = "text-gray-400") => (
    <div className="flex items-center gap-4 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
      <div className={`p-2 rounded-lg bg-gray-50 ${iconColor}`}>
        <Icon size={18} />
      </div>
      <div>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">
          {label}
        </p>
        <p className="text-sm font-semibold text-gray-700">
          {value || "Chưa cập nhật"}
        </p>
      </div>
    </div>
  );

  const handleStatusChange = (studentId, newStatus) => {
    if (!canEdit) return;
    setAttendanceList((prev) =>
      prev.map((item) =>
        item.student_id === studentId ? { ...item, status: newStatus } : item
      )
    );
  };

  const handleNoteChange = (studentId, value) => {
    if (!canEdit) return;
    setNoteMap((prev) => ({ ...prev, [studentId]: value }));
  };

  const filteredList = attendanceList.filter((s) =>
    String(s.full_name || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const presentCount = attendanceList.filter((s) => s.status === "Present").length;
  const absentCount = countAbsentStatuses(attendanceList);
  const lateCount = attendanceList.filter((s) => s.status === "Late").length;

  const handleSave = async () => {
    if (!canEdit) {
      showErrorToast(
        !isTodayDate
          ? `Chỉ có thể cập nhật trong vòng ${editWindowDays} ngày (kèm lý do).`
          : "Chỉ giáo viên phụ trách lớp hoặc Super Admin mới được lưu điểm danh / báo giảng."
      );
      return;
    }
    if (requiresEditReason && !String(editReason || "").trim()) {
      showErrorToast("Vui lòng nhập lý do điểm danh bù / cập nhật.");
      return;
    }
    if (!classId || !checkDate) {
      showErrorToast("Thiếu thông tin lớp hoặc ngày buổi học.");
      return;
    }
    setSaving(true);
    try {
      const records = attendanceList.map((r) => ({
        student_id: r.student_id,
        status: r.status,
        teacher_note: noteMap[r.student_id] ?? r.teacher_note ?? "",
      }));
      await saveAttendanceSheet({
        class_id: classId,
        date: checkDate,
        records,
        session_note: sessionNote,
        edit_reason: requiresEditReason ? editReason.trim() : undefined,
      });
      showSuccessToast("Đã lưu điểm danh và nội dung báo giảng!");
      onClose?.();
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message || "Không thể lưu điểm danh / báo giảng"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white w-full max-w-4xl rounded-[2rem] shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
        >
          <div className="relative p-6 border-b border-gray-100 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-4 min-w-0">
              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-black border shadow-sm shrink-0 ${theme.bg} ${theme.text} ${theme.border}`}
              >
                {event.subject?.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-gray-800 truncate">
                  {event.class}
                </h2>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-[10px] font-bold uppercase">
                    Môn: {event.subject}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${status.color}`}
                  >
                    {status.label}
                  </span>
                  {checkDate ? (
                    <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-[10px] font-bold">
                      Buổi: {checkDate}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>

            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-full transition-all shrink-0"
            >
              <X size={24} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <h3 className="text-[11px] font-black text-red-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <div className="w-1 h-4 bg-red-500 rounded-full" /> Chi tiết
                    giảng dạy
                  </h3>
                  <div className="space-y-1">
                    {infoItem(User, "Giáo viên phụ trách", event.teacher, "text-blue-500")}
                    {infoItem(
                      Clock,
                      "Thời gian học",
                      `${formatHour(event.start)} - ${formatHour(event.end)}`,
                      "text-purple-500"
                    )}
                    {infoItem(
                      Users,
                      "Sĩ số lớp",
                      `${event.students} học sinh`,
                      "text-orange-500"
                    )}
                    {infoItem(BookOpen, "Khóa học", event.course, "text-indigo-500")}
                    {infoItem(
                      CalendarDays,
                      "Hiệu lực lớp (theo hồ sơ)",
                      validityLabel,
                      "text-teal-600"
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <h3 className="text-[11px] font-black text-red-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <div className="w-1 h-4 bg-red-500 rounded-full" /> Địa điểm
                  </h3>
                  <div className="space-y-1 bg-gray-50/50 p-2 rounded-2xl border border-gray-100">
                    {infoItem(
                      Home,
                      "Hình thức lớp",
                      event.venue === "school"
                        ? event.school_name
                          ? `Tại trường — ${event.school_name}`
                          : "Tại trường"
                        : "Trung tâm",
                      "text-amber-600"
                    )}
                    {infoItem(Home, "Phòng học", event.room, "text-green-600")}
                    {infoItem(MapPin, "Cơ sở", event.branch, "text-red-600")}
                  </div>
                </div>

                <div>
                  <h3 className="text-[11px] font-black text-red-500 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <div className="w-1 h-4 bg-red-500 rounded-full" /> Ghi chú
                    buổi dạy
                  </h3>
                  <div className="relative group">
                    <FileText size={16} className="absolute top-3 left-3 text-gray-400" />
                    <textarea
                      className="w-full border border-gray-200 rounded-2xl pl-10 pr-4 py-3 text-sm focus:ring-4 focus:ring-red-500/5 focus:border-red-500 outline-none min-h-[100px] bg-white transition-all font-medium text-gray-700 shadow-sm disabled:bg-gray-50 disabled:cursor-not-allowed"
                      placeholder="Nội dung bài dạy, dặn dò bài tập..."
                      value={sessionNote}
                      onChange={(e) => setSessionNote(e.target.value)}
                      disabled={!canEdit || fetching}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Điểm danh nhúng */}
            <div className="border border-gray-100 rounded-[1.75rem] overflow-hidden bg-slate-50/40">
              <div className="px-5 py-4 bg-white border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center justify-center text-white shadow-md shadow-red-100">
                    <UserCheck size={20} />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-gray-800 uppercase tracking-tight">
                      Điểm danh buổi học
                    </h3>
                    <p className="text-[11px] text-gray-400 font-medium">
                      {!canEdit
                        ? isTodayDate
                          ? "Chỉ xem — bạn không phải giáo viên phụ trách lớp này"
                          : `Ngày quá khứ — chỉ xem hoặc cập nhật trong ${editWindowDays} ngày`
                        : requiresEditReason
                          ? "Điểm danh bù — cần ghi lý do trước khi lưu"
                          : "Giáo viên phụ trách / Super Admin có thể cập nhật"}
                    </p>
                  </div>
                </div>
                {!canEdit ? (
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-100 px-3 py-1.5 rounded-full">
                    <Lock size={12} /> Chỉ xem
                  </span>
                ) : null}
              </div>

              <div className="px-5 py-3 border-b border-gray-100 bg-white/70 space-y-3">
                <div className="relative">
                  <Search
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={16}
                  />
                  <input
                    type="text"
                    placeholder="Tìm tên học viên..."
                    className="w-full bg-white border border-gray-200 rounded-xl pl-10 pr-4 py-2 text-sm outline-none focus:border-red-400 font-medium"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap items-center justify-center gap-4 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                  <span className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-green-500" /> {presentCount} Hiện diện
                  </span>
                  <span className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-orange-500" /> {lateCount} Muộn
                  </span>
                  <span className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-500" /> {absentCount} Vắng
                  </span>
                </div>
                {requiresEditReason && canEdit ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2">
                    <div className="flex items-center gap-2 text-amber-800">
                      <AlertTriangle size={14} />
                      <p className="text-[11px] font-bold">Lý do điểm danh bù (bắt buộc)</p>
                    </div>
                    <textarea
                      value={editReason}
                      onChange={(e) => setEditReason(e.target.value)}
                      placeholder="Nhập lý do cập nhật..."
                      rows={2}
                      className="w-full rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm outline-none focus:border-amber-400"
                    />
                  </div>
                ) : null}
              </div>

              <div className="p-3 md:p-4 max-h-[320px] overflow-y-auto custom-scrollbar">
                {fetching && (
                  <div className="flex justify-center py-10">
                    <Loader2 className="animate-spin text-red-500" size={28} />
                  </div>
                )}
                {!fetching && loadError && (
                  <p className="text-center text-red-600 py-8 text-sm font-medium">
                    {loadError}
                  </p>
                )}
                {!fetching && !loadError && filteredList.length === 0 && (
                  <p className="text-center text-gray-500 py-8 text-sm font-medium">
                    Lớp chưa có học viên đăng ký. Thêm học viên vào lớp trước khi
                    điểm danh.
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
                  {!fetching &&
                    filteredList.map((student, idx) => (
                      <div
                        key={student.student_id}
                        className={`p-3 md:p-4 rounded-2xl border transition-all flex items-center justify-between ${
                          isAbsentStatus(student.status)
                            ? student.status === "AbsentExcused"
                              ? "bg-sky-50 border-sky-100"
                              : "bg-red-50 border-red-100"
                            : student.status === "Late"
                              ? "bg-orange-50/50 border-orange-100"
                              : "bg-white border-gray-100"
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className="hidden md:block text-[10px] font-black text-gray-300 w-4">
                            {idx + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <p
                              className={`text-sm font-bold uppercase tracking-tight truncate ${
                                isAbsentStatus(student.status)
                                  ? student.status === "AbsentExcused"
                                    ? "text-sky-700"
                                    : "text-red-700"
                                  : "text-gray-700"
                              }`}
                            >
                              {student.full_name}
                            </p>
                            <input
                              type="text"
                              placeholder="Ghi chú học viên..."
                              value={
                                noteMap[student.student_id] ??
                                student.teacher_note ??
                                ""
                              }
                              disabled={!canEdit}
                              className="text-[10px] bg-transparent border-none outline-none text-gray-400 focus:text-gray-600 font-medium w-full disabled:cursor-not-allowed"
                              onChange={(e) =>
                                handleNoteChange(student.student_id, e.target.value)
                              }
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-4 p-1 bg-gray-100 rounded-xl gap-1 ml-2 shrink-0">
                          {STATUS_BTNS.map((btn) => (
                            <button
                              key={btn.val}
                              type="button"
                              disabled={!canEdit}
                              onClick={() =>
                                handleStatusChange(student.student_id, btn.val)
                              }
                              className={`p-2 rounded-lg transition-all disabled:opacity-60 ${
                                student.status === btn.val
                                  ? btn.active
                                  : "text-gray-400"
                              }`}
                            >
                              <btn.icon className="w-[18px] h-[18px]" />
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-gray-50 bg-gray-50/30 flex justify-end gap-3 shrink-0">
            <button
              onClick={onClose}
              className="px-6 py-3 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition-all uppercase text-xs tracking-wider"
            >
              Đóng
            </button>
            <button
              className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl flex items-center gap-2 shadow-lg shadow-red-100 transition-all active:scale-95 uppercase text-xs tracking-wider disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSave}
              disabled={!canEdit || saving || fetching}
            >
              {saving ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <CheckCircle2 size={18} />
              )}
              {saving ? "Đang lưu..." : "Lưu báo giảng"}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EventDetailModal;
